import React, { useState } from 'react';
import { X, Search, CheckCircle } from 'lucide-react';

const SelectionModal = ({
    isOpen,
    onClose,
    title,
    options = [],
    onSelect,
    selectedValue,
    searchPlaceholder = "Cari pilihan...",
    showSearch = false,
    subtitle = "",
    multiple = false
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    if (!isOpen) return null;

    // Filter options based on search
    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (opt.description && opt.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleOptionClick = (value) => {
        if (multiple) {
            const currentSelected = Array.isArray(selectedValue) ? selectedValue : [];
            const newSelected = currentSelected.includes(value)
                ? currentSelected.filter(v => v !== value)
                : [...currentSelected, value];
            onSelect(newSelected);
        } else {
            onSelect(value);
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            {/* Backdrop with blur */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className="relative w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col animate-in slide-in-from-bottom-5 sm:slide-in-from-bottom-10 fade-in duration-300">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="text-sm text-gray-500 mt-0.5">
                                {subtitle}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Search Bar (Optional) */}
                {showSearch && (
                    <div className="p-4 bg-gray-50/50 border-b border-gray-100">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm text-gray-900"
                                autoFocus
                            />
                        </div>
                    </div>
                )}

                {/* Options Grid */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar">
                    {filteredOptions.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {filteredOptions.map((option) => {
                                const isSelected = multiple
                                    ? (Array.isArray(selectedValue) && selectedValue.includes(option.value))
                                    : selectedValue === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => handleOptionClick(option.value)}
                                        className={`group relative p-4 rounded-xl border-2 text-left transition-all duration-200 hover:shadow-md ${isSelected
                                            ? 'border-indigo-500 bg-indigo-50'
                                            : 'border-gray-100 bg-white hover:border-indigo-200'
                                            }`}
                                    >
                                        <div className="flex items-start gap-4">
                                            {/* Icon */}
                                            {option.icon && (
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ${isSelected
                                                    ? 'bg-indigo-500 text-white'
                                                    : 'bg-gray-100 text-gray-500 group-hover:bg-indigo-100 group-hover:text-indigo-600'
                                                    }`}>
                                                    {option.icon}
                                                </div>
                                            )}

                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start">
                                                    <h4 className={`font-semibold text-sm mb-1 truncate ${isSelected ? 'text-indigo-900' : 'text-gray-900'
                                                        }`}>
                                                        {option.label}
                                                    </h4>
                                                    {isSelected && (
                                                        <CheckCircle className="text-indigo-500 w-5 h-5 shrink-0" />
                                                    )}
                                                </div>

                                                {option.description && (
                                                    <p className={`text-xs leading-relaxed line-clamp-2 ${isSelected ? 'text-indigo-700' : 'text-gray-500'
                                                        }`}>
                                                        {option.description}
                                                    </p>
                                                )}

                                                {/* Tags (optional) */}
                                                {option.tags && (
                                                    <div className="flex gap-1 mt-2">
                                                        {option.tags.map((tag, idx) => (
                                                            <span key={idx} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-600">
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Search className="text-gray-400" size={24} />
                            </div>
                            <h3 className="text-gray-900 font-medium mb-1">
                                Tidak ditemukan
                            </h3>
                            <p className="text-gray-500 text-sm">
                                Coba kata kunci pencarian lain
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer (For Multiple Mode) */}
                {multiple && (
                    <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
                        <button
                            onClick={onClose}
                            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-indigo-500/30"
                        >
                            Selesai ({Array.isArray(selectedValue) ? selectedValue.length : 0} Terpilih)
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelectionModal;
